import { computed, Directive, effect, inject, OnInit, ResourceRef, signal, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AvailableTransitionsClient, TransitionMiniDto, TransitionRequestDto, TransitionsClient } from '@unity/clients';
import { DetailFormService } from '@unity/services/detail-form.service';
import { ErrorService } from '@unity/services/error.service';
import { LayoutService } from '@unity/services/layout.service';
import { PermissionService } from '@unity/services/permission.service';
import { FormEditState, FormGroupControlSchema, UnityDto, UnityEntitlementPath } from '@unity/utils/types';
import { Observable, take } from 'rxjs';

@Directive() // See https://github.com/angular/angular/issues/37769
export abstract class AbstractManageComponent<
  TDto extends Readonly<UnityDto>,
  // Needed for interop
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TUpdateDto extends Record<string, any>,
  TSchema extends FormGroupControlSchema<TUpdateDto>,
> implements OnInit
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly detailFormService = inject(DetailFormService);
  private readonly permissionService = inject(PermissionService);
  private readonly errorService = inject(ErrorService);
  protected readonly layoutService = inject(LayoutService);
  protected readonly transitionsClient = inject(TransitionsClient);
  protected readonly availableTransitionsClient = inject(AvailableTransitionsClient);

  /* Abstract */
  protected abstract availableTransitionsFn$: (resourceId: number) => Observable<TransitionMiniDto[]>;

  protected abstract updateResourceFn$: (resourceId: number, form: FormGroup<TSchema>) => Observable<unknown>;
  protected abstract deleteResourceFn$: (resourceId: number) => Observable<unknown>;
  protected abstract doTransitionFn$: (resourceId: number, transition: TransitionRequestDto) => Observable<unknown>;

  protected abstract newControlsFn(dto?: TDto): TSchema;

  protected abstract resourceName: string;
  protected abstract readonly $resourceId: Signal<number>;
  protected abstract editEntitlement: UnityEntitlementPath;
  protected abstract deleteEntitlement: UnityEntitlementPath;
  protected abstract transitionEntitlement: UnityEntitlementPath;

  protected abstract $contentHeader: Signal<string>;

  protected abstract mainResource: ResourceRef<TDto | undefined>;

  /* Resources */
  private availableTransitionsResource = rxResource({
    params: () => {
      const isEntitledToTransition = this.$isEntitledToTransition();
      const resourceId = this.$resourceId();
      return resourceId && isEntitledToTransition ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.availableTransitionsFn$(resourceId),
  });

  protected readonly detailCardGridLayoutGap = this.layoutService.detailCardGridLayoutGap;

  /* Signals */
  protected readonly $workflowActionsDisabled = signal<boolean>(false);
  protected readonly $updateLoading = signal<boolean | undefined>(undefined);
  protected readonly $resourceLoading = computed(() => this.mainResource.isLoading());
  protected readonly $availableTransitions = computed(() => this.availableTransitionsResource.value());

  protected readonly $isEntitledToEdit = computed(() => {
    const resource = this.$resource();
    const isEditableStateOrNotStateful = resource?.current_state == undefined || resource?.current_state?.is_editable;
    return isEditableStateOrNotStateful && this.permissionService.userIsEntitled(this._editEntitlement);
  });
  protected readonly $isEntitledToTransition = computed(() => this.permissionService.userIsEntitled(this._transitionEntitlement));

  protected readonly $resource = computed(() => this.mainResource.value());

  protected readonly $formGroup = computed(() => {
    const dto = this.mainResource.value();
    return this.newFormGroup(dto);
  });

  public constructor() {
    effect(() => {
      const error = this.mainResource.error();
      if (error) {
        this.errorService.handleNotFound(error);
      }
    });

    effect(() => {
      const v = this.$resource();
      if (v) {
        this.refreshTransitions();
      }
    });
  }

  /* Getters */
  private get _editEntitlement() {
    return this.editEntitlement;
  }

  private get _transitionEntitlement() {
    return this.transitionEntitlement;
  }

  private _getNewSchemaFn(dto?: TDto) {
    return this.newControlsFn(dto);
  }

  /* Methods */
  private newFormGroup(dto?: TDto) {
    return this.formBuilder.group<TSchema>(this._getNewSchemaFn(dto));
  }

  private refreshResource() {
    this.mainResource.reload();
  }

  private refreshTransitions() {
    this.availableTransitionsResource.reload();
  }

  private updateResource() {
    const formGroup = this.$formGroup() as unknown as FormGroup<TSchema>;
    const resourceId = this.$resourceId();

    if (!resourceId) {
      throw new Error('Invalid page state, please reload');
    }
    this.$updateLoading.set(true);

    this.detailFormService.updateResource$(formGroup, this.updateResourceFn$(resourceId, formGroup)).subscribe({
      next: () => this.refreshResource(),
      complete: () => this.$updateLoading.set(false),
    });
  }

  private deleteResource() {
    const resourceId = this.$resourceId();
    if (!resourceId) {
      throw new Error('Invalid page state, please reload');
    }
    this.detailFormService.deleteResource$(this.deleteResourceFn$(resourceId), this.resourceName).subscribe();
  }

  private performTransition(transition: TransitionRequestDto) {
    const resourceId = this.$resourceId();
    if (!resourceId) {
      throw new Error('Invalid page state, please reload');
    }
    this.doTransitionFn$(resourceId, transition)
      .pipe(take(1))
      .subscribe(() => this.refreshResource());
  }

  protected onSave() {
    this.updateResource();
  }

  protected onDelete() {
    this.deleteResource();
  }

  protected onWorkflow(transition: TransitionRequestDto) {
    this.performTransition(transition);
  }

  protected toggleWorkflowActions(state: FormEditState) {
    this.$workflowActionsDisabled.set(state.editing === true);
  }

  public ngOnInit() {
    this.refreshResource();
  }
}
